import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearPortalLocalStorage } from '@rsos/base-utils/clearPortalLocalStorage';
import { BASEMAP_ADDON_GIS } from '@rsos/constants/basemapAddonNames';
import { ESRI } from '@rsos/constants/mapProviders';
import {
  clearLayerMaps,
  fetchGISServices,
  GISServiceTypes,
  gisLayerFor,
  loadGISLayer,
  resetLayerLoadedState,
  selectGISServices,
  selectGISLayersRaw,
} from '@rsos/reduxDataStore';
import {
  logout,
  selectMapProvider,
  swallowThunkRejection,
} from '@rsos/sinatra';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';

/** Poll interval and proactive renewal window (must stay aligned: we check often enough to catch the buffer). */
const TOKEN_RENEWAL_PERIOD_MINUTES = 15;
const TOKEN_RENEWAL_INTERVAL_MS = 1000 * 60 * TOKEN_RENEWAL_PERIOD_MINUTES;
/** Renew when within this many seconds of absolute expiry. */
const TOKEN_RENEWAL_BUFFER_SEC = 60 * TOKEN_RENEWAL_PERIOD_MINUTES;

/**
 * Hook for managing GIS services and layer loading when the agency has GIS capability enabled.
 *
 * ## Behavior Overview
 *
 * **Initial Mount:**
 * - Fetches GIS service definitions if GIS addon is enabled
 * - Every Andromeda GIS overlay loads immediately (same treatment the
 *   authoritative flag used to gate). Visibility is still `auto_checked`.
 * - On-demand session layers still load when the user adds them
 *
 * **Provider Switching (ESRI <-> Google):**
 * - When switching to Google: layers are deactivated (via clearAllGISServices)
 * - When switching back to ESRI: ESRI layer objects are recreated for the new MapView
 * - Service definitions are preserved in Redux to avoid unnecessary API calls
 *
 * **GIS Addon Toggle:**
 * - When toggled OFF: layers become invisible
 * - When toggled ON: triggers layer reload by resetting loaded state in Redux
 * - Clears stale ESRI layer object references from previous MapView
 *
 * **Token Renewal:**
 * - Polls every 15 minutes and refetches when any identity is within 15 minutes of expiry
 *   (token TTL is a duration from fetch time; see MappedGISIdentity.tokenFetchedAtMs)
 *
 * ## Refs
 *
 * - `essentialLayersLoadedRef`: Tri-state ref (null/false/true) for tracking layer loading:
 *   - null: waiting for fetchGISServices to complete
 *   - false: ready to load GIS overlays
 *   - true: initial GIS overlays already loaded
 *
 * - `initialLoadTriggeredRef`: Prevents duplicate fetches within the same mount cycle
 *
 * - `prevMapProviderRef`: Tracks previous map provider to detect provider switches
 */
const useGISToken = () => {
  const dispatch = useDispatch();

  const orgName = useSelector(state => state.sinatra.user.currentOrg.name);
  const mapProvider = useSelector(selectMapProvider);
  const prevMapProviderRef = useRef(mapProvider);

  const hasGISServicesAccess = useSelector(state =>
    checkGISServicesAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const mapAddons = useSelector(state => state.sinatra.ui.mapAddons);
  const isGISAddonEnabled = mapAddons?.includes(BASEMAP_ADDON_GIS);

  const gisIdentities = useSelector(state => state.gisServices.gisIdentities);
  const services = useSelector(selectGISServices);
  const layers = useSelector(selectGISLayersRaw);
  const gisServicesLoading = useSelector(state => state.gisServices?.loading);

  const essentialLayersLoadedRef = useRef(null);
  const initialLoadTriggeredRef = useRef(false);
  const hadGISAccessRef = useRef(false);

  useEffect(() => {
    if (hasGISServicesAccess && gisIdentities?.length > 0) {
      hadGISAccessRef.current = true;
    }
  }, [hasGISServicesAccess, gisIdentities]);

  const forceLogout = useCallback(() => {
    dispatch(logout()).catch(swallowThunkRejection);
    clearPortalLocalStorage();
    window.location = '/';
  }, [dispatch]);

  useEffect(() => {
    if (
      !hasGISServicesAccess ||
      !isGISAddonEnabled ||
      initialLoadTriggeredRef.current
    ) {
      return;
    }
    initialLoadTriggeredRef.current = true;
    clearLayerMaps();

    if (services.length > 0) {
      dispatch(resetLayerLoadedState());
      essentialLayersLoadedRef.current = false;
      return;
    }

    essentialLayersLoadedRef.current = null;
    dispatch(fetchGISServices({ orgName, shouldLoadLayers: false }));
  }, [
    dispatch,
    hasGISServicesAccess,
    isGISAddonEnabled,
    orgName,
    services.length,
  ]);

  useEffect(() => {
    if (gisServicesLoading && essentialLayersLoadedRef.current === null) {
      essentialLayersLoadedRef.current = false;
    }

    if (
      gisServicesLoading ||
      essentialLayersLoadedRef.current === null ||
      essentialLayersLoadedRef.current === true ||
      services.length === 0 ||
      layers.length === 0
    ) {
      return;
    }

    const unloadedLayers = layers.filter(
      l =>
        (!l.loaded || !gisLayerFor(l)) &&
        !l.loading &&
        l.service?.service_type !== GISServiceTypes.geocode,
    );

    if (unloadedLayers.length === 0) {
      essentialLayersLoadedRef.current = true;
      return;
    }

    essentialLayersLoadedRef.current = true;

    unloadedLayers.forEach(layer => {
      const service = services.find(s => s.gisLayerID === layer.id);
      if (service) {
        dispatch(loadGISLayer({ service }));
      }
    });
  }, [dispatch, gisServicesLoading, services, layers]);

  useEffect(() => {
    const prevProvider = prevMapProviderRef.current;
    prevMapProviderRef.current = mapProvider;

    if (
      hasGISServicesAccess &&
      isGISAddonEnabled &&
      mapProvider === ESRI &&
      prevProvider &&
      prevProvider !== ESRI
    ) {
      clearLayerMaps();
      essentialLayersLoadedRef.current = null;
      initialLoadTriggeredRef.current = true;
      dispatch(fetchGISServices({ orgName, shouldLoadLayers: false }));
    }
  }, [dispatch, hasGISServicesAccess, isGISAddonEnabled, mapProvider, orgName]);

  useEffect(() => {
    if (!hasGISServicesAccess || !isGISAddonEnabled || !gisIdentities?.length) {
      return;
    }

    const intervalId = setInterval(async () => {
      const nowSec = Date.now() / 1000;

      const shouldRenew = gisIdentities.some(identity => {
        const expiresIn = identity?.tokenExpiresIn;
        const fetchedAtMs = identity?.tokenFetchedAtMs;

        if (expiresIn == null) return false;
        if (fetchedAtMs == null) return true;

        const absoluteExpirySec = fetchedAtMs / 1000 + expiresIn;
        return nowSec >= absoluteExpirySec - TOKEN_RENEWAL_BUFFER_SEC;
      });

      if (!shouldRenew) return;

      const result = await dispatch(
        fetchGISServices({ orgName, shouldLoadLayers: false }),
      );

      if (fetchGISServices.rejected.match(result) && hadGISAccessRef.current) {
        forceLogout();
      }
    }, TOKEN_RENEWAL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [
    dispatch,
    hasGISServicesAccess,
    isGISAddonEnabled,
    gisIdentities,
    orgName,
    forceLogout,
  ]);
};

export default useGISToken;
